// RegionsAdminPage — super-admin only. Manage zipcodes per region.
//
// Features:
//   - List existing regions with zip count
//   - Per-region: paste-list textarea to bulk add or remove zips
//   - Test-an-address tool: type a zip, see which region it resolves to
//   - Active toggle per region
//
// All zipcode data is operator-only — never exposed to customers/drivers.

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../../components/Navbar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Plus, Minus, Search, MapPin, Bell } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Arrival-push threshold dropdown values (must match backend
// regions_mod.ARRIVAL_THRESHOLD_ALLOWED). "" = use default (5 min).
const ARRIVAL_THRESHOLD_OPTIONS = [
  { value: '', label: 'Default (5 min)' },
  { value: '3', label: '3 min' },
  { value: '5', label: '5 min' },
  { value: '7', label: '7 min' },
  { value: '10', label: '10 min' },
  { value: '15', label: '15 min' },
];

export function RegionsAdminPage() {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});  // {regionCode: "20001\n20002"}
  const [busy, setBusy] = useState({});      // {regionCode: bool}
  const [testZip, setTestZip] = useState('');
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/regions?include_inactive=true`, {
        withCredentials: true,
      });
      setRegions(data?.regions || []);
    } catch (err) {
      toast.error('Failed to load regions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setDraft = (code, value) => setDrafts((d) => ({ ...d, [code]: value }));
  const setBusyFor = (code, val) => setBusy((b) => ({ ...b, [code]: val }));

  const parseZips = (text) => {
    if (!text) return [];
    return Array.from(new Set(
      text.split(/[\s,;]+/).map((z) => z.trim()).filter(Boolean)
    ));
  };

  const addZips = async (code) => {
    const zips = parseZips(drafts[code]);
    if (!zips.length) {
      toast.warning('No zipcodes to add');
      return;
    }
    setBusyFor(code, true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/regions/${code}/zipcodes/add`,
        { zipcodes: zips },
        { withCredentials: true }
      );
      const report = data._add_report || {};
      const added = (report.added || []).length;
      const dupes = (report.already_in_region || []).length;
      const conflicts = report.conflicts || [];

      // Build a multi-line message — conflicts get the most attention
      const lines = [];
      if (added > 0) lines.push(`✓ ${added} added`);
      if (dupes > 0) lines.push(`· ${dupes} already in ${code.toUpperCase()}`);
      if (conflicts.length > 0) {
        lines.push(
          `✗ ${conflicts.length} BLOCKED (already in another region): ` +
          conflicts.map(c => `${c.zip}→${c.current_region.toUpperCase()}`).join(', ')
        );
      }
      const summary = lines.length ? lines.join(' · ') : 'No changes';

      if (conflicts.length > 0) {
        toast.error(summary, { duration: 8000 });
      } else {
        toast.success(`${code.toUpperCase()}: ${summary}`);
      }
      setDraft(code, '');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Add failed');
    } finally {
      setBusyFor(code, false);
    }
  };

  const removeZips = async (code) => {
    const zips = parseZips(drafts[code]);
    if (!zips.length) {
      toast.warning('No zipcodes to remove');
      return;
    }
    if (!window.confirm(`Remove ${zips.length} zipcode(s) from ${code.toUpperCase()}?`)) return;
    setBusyFor(code, true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/regions/${code}/zipcodes/remove`,
        { zipcodes: zips },
        { withCredentials: true }
      );
      toast.info(`${code.toUpperCase()} now has ${data.zipcodes?.length || 0} zipcodes`);
      setDraft(code, '');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remove failed');
    } finally {
      setBusyFor(code, false);
    }
  };

  const saveArrivalThreshold = async (code, rawValue) => {
    // rawValue: '' (default) or '3'|'5'|'7'|'10'|'15'
    const minutes = rawValue === '' ? null : parseInt(rawValue, 10);
    setBusyFor(code, true);
    try {
      await axios.patch(
        `${API_URL}/api/regions/${code}/arrival-threshold`,
        { minutes },
        { withCredentials: true }
      );
      toast.success(
        minutes === null
          ? `${code.toUpperCase()}: arrival threshold reset to default (5 min)`
          : `${code.toUpperCase()}: arrival threshold set to ${minutes} min`
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusyFor(code, false);
    }
  };

  const runTest = async () => {
    setTestResult(null);
    if (!testZip.trim()) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/regions/resolve`, {
        params: { zip: testZip.trim() },
        withCredentials: true,
      });
      setTestResult({ ok: true, ...data });
    } catch (err) {
      setTestResult({ ok: false, message: err.response?.data?.detail || 'Resolution failed' });
    }
  };

  return (
    <div className="min-h-screen bg-[#050003] dispatch-safe-top" data-testid="regions-admin-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/dispatch" className="inline-flex items-center text-[#4B6B52] hover:text-[#39FF14] font-mono text-sm mb-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> BACK TO DISPATCH
          </Link>
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text">
            {'>'} REGIONS_ADMIN
          </h1>
          <p className="text-[#4B6B52] font-mono text-sm mt-1">
            // SUPER-ADMIN ONLY — operational metadata, hidden from customers + drivers
          </p>
        </div>

        {/* Test-an-address tool */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6 mb-8">
          <p className="text-[#39FF14] font-mono text-sm font-bold uppercase mb-3">// TEST AN ADDRESS</p>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-1">ZIPCODE OR FULL ADDRESS</p>
              <Input
                value={testZip}
                onChange={(e) => setTestZip(e.target.value)}
                placeholder="20001 or 'foo bar 20001-1234'"
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                data-testid="test-zip-input"
                onKeyDown={(e) => { if (e.key === 'Enter') runTest(); }}
              />
            </div>
            <Button
              onClick={runTest}
              className="bg-[#00F0FF] text-[#050003] font-bold hover:bg-[#7CECFF] rounded-none"
              data-testid="test-zip-btn"
            >
              <Search className="w-4 h-4 mr-2" /> RESOLVE
            </Button>
          </div>
          {testResult && (
            <div
              className={`mt-3 p-3 border font-mono text-xs ${
                testResult.ok
                  ? 'border-[#39FF14] text-[#39FF14] bg-[rgba(57,255,20,0.04)]'
                  : 'border-[#FF3B30] text-[#FF3B30] bg-[rgba(255,59,48,0.04)]'
              }`}
              data-testid="test-zip-result"
            >
              {testResult.ok
                ? <>→ {testResult.code.toUpperCase()} ({testResult.name})</>
                : <>✗ {testResult.message}</>
              }
            </div>
          )}
        </div>

        {/* Region cards */}
        {loading ? (
          <p className="text-[#4B6B52] font-mono text-sm">// loading...</p>
        ) : regions.length === 0 ? (
          <p className="text-[#4B6B52] font-mono text-sm">// no regions configured</p>
        ) : (
          <div className="space-y-6">
            {regions.map((r) => (
              <div
                key={r.code}
                className="bg-[#0C0612] border border-[#2A0F3D] p-6"
                data-testid={`region-card-${r.code}`}
              >
                <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#39FF14]" />
                      <h2 className="text-[#39FF14] font-mono text-lg font-bold uppercase">
                        {r.name} ({r.code})
                      </h2>
                      {!r.active && (
                        <span className="px-2 py-0.5 border border-[#FF9F0A] text-[#FF9F0A] font-mono text-[10px] uppercase">
                          INACTIVE
                        </span>
                      )}
                    </div>
                    <p className="text-[#4B6B52] font-mono text-xs mt-1">
                      // {r.zipcodes?.length || 0} zipcode(s) configured
                    </p>
                  </div>
                </div>

                <div className="mb-3">
                  <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-1">CURRENT ZIPCODES</p>
                  <div
                    className="bg-[#050003] border border-[#2A0F3D] p-3 max-h-32 overflow-auto font-mono text-xs text-[#A3C7A8] flex flex-wrap gap-1"
                    data-testid={`zipcodes-${r.code}`}
                  >
                    {(r.zipcodes || []).length === 0 ? (
                      <span className="text-[#4B6B52]">// none</span>
                    ) : (
                      (r.zipcodes || []).map((z) => (
                        <span key={z} className="px-1.5 py-0.5 bg-[rgba(57,255,20,0.06)] border border-[#2A0F3D]">
                          {z}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* Arrival-push threshold (Feb 2026 — flag-gated 'driver X min away') */}
                <div className="mb-3 bg-[#050003] border border-[#2A0F3D] p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <Bell className="w-3.5 h-3.5 text-[#39FF14] mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-[#A3C7A8] font-mono text-xs uppercase">
                        ARRIVAL PUSH THRESHOLD
                      </p>
                      <p className="text-[#4B6B52] font-mono text-[10px] mt-0.5">
                        // requires flag <span className="text-[#39FF14]">push.customer.arrival_threshold</span> = ON
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={
                        Number.isInteger(r.arrival_threshold_min)
                          ? String(r.arrival_threshold_min)
                          : ''
                      }
                      onChange={(e) => saveArrivalThreshold(r.code, e.target.value)}
                      disabled={busy[r.code]}
                      data-testid={`arrival-threshold-${r.code}`}
                      className="bg-[#0C0612] border border-[#2A0F3D] text-[#39FF14] font-mono text-sm px-3 py-1.5 focus:border-[#39FF14] focus:outline-none rounded-none disabled:opacity-50"
                    >
                      {ARRIVAL_THRESHOLD_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <span className="text-[#4B6B52] font-mono text-xs">
                      Fires once per order when ETA drops below this.
                    </span>
                  </div>
                </div>

                <div>
                  <p className="text-[#A3C7A8] font-mono text-xs uppercase mb-1">
                    ADD / REMOVE (paste one or many — commas, spaces or newlines)
                  </p>
                  <Textarea
                    value={drafts[r.code] || ''}
                    onChange={(e) => setDraft(r.code, e.target.value)}
                    placeholder="20001\n20002\n21043"
                    rows={3}
                    className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none resize-none"
                    data-testid={`zipcodes-input-${r.code}`}
                  />
                  <div className="flex gap-2 mt-2 justify-end">
                    <Button
                      variant="outline"
                      onClick={() => removeZips(r.code)}
                      disabled={busy[r.code] || !drafts[r.code]?.trim()}
                      className="border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.08)] rounded-none font-mono"
                      data-testid={`remove-zips-${r.code}`}
                    >
                      <Minus className="w-3 h-3 mr-1" /> REMOVE
                    </Button>
                    <Button
                      onClick={() => addZips(r.code)}
                      disabled={busy[r.code] || !drafts[r.code]?.trim()}
                      className="bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] disabled:opacity-40 rounded-none"
                      data-testid={`add-zips-${r.code}`}
                    >
                      <Plus className="w-3 h-3 mr-1" /> ADD
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default RegionsAdminPage;
